import { useState } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import BookingModal from '../../components/feature/BookingModal';
import { SEO } from '../../utils/seo';

export default function LocationPage() {
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);

  const schema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "대전에어렌트카",
    "description": "대전 전 지역 24시간 렌트카 서비스. 소형차, 중형차, 대형차, SUV, 승합차 등 다양한 차량 보유. 세종, 논산, 계룡, 공주 지역도 서비스 가능.",
    "url": `${import.meta.env.VITE_SITE_URL || 'https://daejeon-rentcar.com'}/location`,
    "telephone": "010-5437-1535",
    "image": `${import.meta.env.VITE_SITE_URL || 'https://daejeon-rentcar.com'}/logo.png`,
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "신갈마로141번길10",
      "addressLocality": "서구",
      "addressRegion": "대전광역시",
      "postalCode": "35222",
      "addressCountry": "KR"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": "36.3504",
      "longitude": "127.3845"
    },
    "openingHoursSpecification": {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": [
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday",
        "Saturday",
        "Sunday"
      ],
      "opens": "00:00",
      "closes": "23:59"
    },
    "priceRange": "₩₩",
    "areaServed": [
      "대전광역시",
      "세종특별자치시",
      "충청남도 공주시",
      "충청남도 논산시",
      "충청남도 계룡시"
    ],
    "sameAs": [
      "https://blog.naver.com/air1535"
    ]
  };

  return (
    <>
      <div className="min-h-screen bg-white">
        <SEO
          title="오시는 길 - 대전에어렌트카 위치 안내"
          description="대전에어렌트카 오시는 길 안내. 대전광역시 서구 신갈마로141번길10. 24시간 운영, 대전 전 지역 무료 배달 서비스. 세종, 논산, 계룡, 공주 지역도 서비스 가능합니다."
          keywords="대전렌트카위치, 대전렌트카오시는길, 대전에어렌트카주소, 대전렌트카지도"
          canonical={`${import.meta.env.VITE_SITE_URL || 'https://daejeon-rentcar.com'}/location`}
          schema={schema}
        />
        <Navbar />
        
        <main className="pt-24 pb-16">
          <div className="max-w-7xl mx-auto px-8">
            <div className="text-center mb-12">
              <h1 className="text-4xl font-bold text-gray-900 mb-4">오시는 길</h1>
              <p className="text-lg text-gray-600">대전에어렌트카를 찾아오시는 방법을 안내해드립니다</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
              <div className="bg-gray-50 rounded-2xl p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                  <i className="ri-map-pin-line text-primary"></i>
                  매장 정보
                </h2>
                
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-building-line text-primary text-xl"></i>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">회사명</h3>
                      <p className="text-gray-600">누리렌트카(주)</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-map-pin-2-line text-primary text-xl"></i>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">주소</h3>
                      <p className="text-gray-600">대전 서구 신갈마로141번길10</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-phone-line text-primary text-xl"></i>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">전화번호</h3>
                      <a href="tel:010-5437-1535" className="text-primary hover:underline">
                        010-5437-1535
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-printer-line text-primary text-xl"></i>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">팩스</h3>
                      <p className="text-gray-600">042-528-6261</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-time-line text-primary text-xl"></i>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">영업시간</h3>
                      <p className="text-gray-600">24시간 운영</p>
                      <p className="text-sm text-gray-500 mt-1">연중무휴</p>
                    </div>
                  </div>
                </div>

                <div className="mt-8 pt-8 border-t border-gray-200">
                  <h3 className="font-semibold text-gray-900 mb-4">교통편 안내</h3>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li className="flex items-start gap-2">
                      <i className="ri-bus-line text-primary mt-1"></i>
                      <span><strong>버스:</strong> 서구청 정류장 하차 후 도보 5분</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-subway-line text-primary mt-1"></i>
                      <span><strong>지하철:</strong> 1호선 서대전네거리역 3번 출구 도보 10분</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-car-line text-primary mt-1"></i>
                      <span><strong>자가용:</strong> 무료 주차 가능</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="bg-gray-50 rounded-2xl p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                  <i className="ri-question-line text-primary"></i>
                  자주 묻는 질문
                </h2>
                
                <div className="space-y-4">
                  <div className="bg-white rounded-lg p-4">
                    <h3 className="font-semibold text-gray-900 mb-2">Q. 예약은 어떻게 하나요?</h3>
                    <p className="text-sm text-gray-600">홈페이지에서 온라인 예약하시거나 전화로 예약 가능합니다.</p>
                  </div>

                  <div className="bg-white rounded-lg p-4">
                    <h3 className="font-semibold text-gray-900 mb-2">Q. 배달 서비스가 있나요?</h3>
                    <p className="text-sm text-gray-600">네, 대전 전 지역 무료 배달 서비스를 제공합니다.</p>
                  </div>

                  <div className="bg-white rounded-lg p-4">
                    <h3 className="font-semibold text-gray-900 mb-2">Q. 필요한 서류는 무엇인가요?</h3>
                    <p className="text-sm text-gray-600">운전면허증, 신분증, 신용카드가 필요합니다.</p>
                  </div>

                  <div className="bg-white rounded-lg p-4">
                    <h3 className="font-semibold text-gray-900 mb-2">Q. 보험은 어떻게 되나요?</h3>
                    <p className="text-sm text-gray-600">모든 차량에 종합보험이 가입되어 있습니다.</p>
                  </div>

                  <div className="bg-white rounded-lg p-4">
                    <h3 className="font-semibold text-gray-900 mb-2">Q. 장기 렌트 할인이 있나요?</h3>
                    <p className="text-sm text-gray-600">7일 이상 장기 렌트 시 특별 할인 혜택을 제공합니다.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="rounded-2xl overflow-hidden shadow-lg">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3214.5!2d127.384!3d36.351!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMzbCsDIxJzAzLjYiTiAxMjfCsDIzJzAyLjQiRQ!5e0!3m2!1sko!2skr!4v1234567890"
                width="100%"
                height="500"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="대전에어렌트카 위치"
              ></iframe>
            </div>

            <div className="mt-8 text-center">
              <a
                href="https://map.naver.com/p/search/대전%20서구%20신갈마로141번길10"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-primary text-white px-8 py-3 rounded-full font-semibold hover:bg-primary/90 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-navigation-line"></i>
                네이버 지도로 길찾기
              </a>
            </div>
          </div>
        </main>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-br from-emerald-50 to-teal-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              지금 바로 예약하고 특별 할인을 받으세요
            </h2>
            <p className="text-xl text-gray-600 mb-10 max-w-2xl mx-auto">
              대전 최고의 렌트카 서비스를 합리적인 가격으로 이용하세요
            </p>
            <button 
              onClick={() => setIsBookingModalOpen(true)}
              className="bg-gray-900 text-white px-12 py-4 rounded-full text-lg font-medium hover:bg-gray-800 transition-all duration-200 hover:shadow-xl cursor-pointer whitespace-nowrap"
            >
              지금 예약하고 할인받기
            </button>
          </div>
        </section>

        <Footer />
      </div>

      <BookingModal 
        isOpen={isBookingModalOpen} 
        onClose={() => setIsBookingModalOpen(false)} 
      />
    </>
  );
}